import { Component, OnDestroy, OnInit } from '@angular/core';
import { JhiEventManager, JhiLanguageService } from '../../shared/core-utils';
import { TranslateService } from '@ngx-translate/core';
import { GermplasmAttribute } from '../../shared/germplasm/model/germplasm.model';
import { GermplasmDetailsContext } from '../germplasm-details.context';
import { GermplasmService } from '../../shared/germplasm/service/germplasm.service';
import { Subscription } from 'rxjs';
import { ModalConfirmComponent } from '../../shared/modal/modal-confirm.component';
import { ActivatedRoute, Router } from '@angular/router';
import { GermplasmAttributeContext } from '../../entities/germplasm/attribute/germplasm-attribute.context';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EDIT_GERMPLASM_PERMISSION, GERMPLASM_AUDIT_PERMISSION, MG_MANAGE_FILES_PERMISSION } from '../../shared/auth/permissions';
import { VariableTypeEnum } from '../../shared/ontology/variable-type.enum';
import { VariableDetails } from '../../shared/ontology/model/variable-details';
import { VariableService } from '../../shared/ontology/service/variable.service';
import { VariableValidationService } from '../../shared/ontology/service/variable-validation.service';
import { FileService } from '../../shared/file/service/file.service';
import { FileDeleteOptionsComponent } from '../../shared/file/component/file-delete-options.component';
import { formatErrorList } from '../../shared/alert/format-error-list';
import { Principal } from '../../shared';
import { AlertService } from '../../shared/alert/alert.service';
import { MAX_ATTRIBUTE_DISPLAY_SIZE } from '../../app.constants';

@Component({
    selector: 'jhi-attributes-pane',
    templateUrl: './attributes-pane.component.html'
})
export class AttributesPaneComponent implements OnInit, OnDestroy {

    protected readonly MAX_ATTRIBUTE_DISPLAY_SIZE = MAX_ATTRIBUTE_DISPLAY_SIZE;

    MODIFY_ATTRIBUTES_PERMISSIONS = [...EDIT_GERMPLASM_PERMISSION, 'MODIFY_ATTRIBUTES'];
    ATTRIBUTES_ACTIONS_PERMISSIONS = [...this.MODIFY_ATTRIBUTES_PERMISSIONS, ...GERMPLASM_AUDIT_PERMISSION];
    GERMPLASM_AUDIT_PERMISSION = GERMPLASM_AUDIT_PERMISSION;

    eventSubscriber: Subscription;
    passportAttributes: GermplasmAttribute[] = [];
    attributes: GermplasmAttribute[] = [];
    variableByAttributeId: { [key: number]: VariableDetails } = {};
    VariableTypeEnum = VariableTypeEnum;
    isFileStorageConfigured: boolean;

    constructor(public languageservice: JhiLanguageService,
                public translateService: TranslateService,
                private eventManager: JhiEventManager,
                private germplasmDetailsContext: GermplasmDetailsContext,
                private germplasmService: GermplasmService,
                private variableService: VariableService,
                private variableValidationService: VariableValidationService,
                private alertService: AlertService,
                private modalService: NgbModal,
                private router: Router,
                private route: ActivatedRoute,
                private germplasmAttributesContext: GermplasmAttributeContext,
                private fileService: FileService,
                private principal: Principal
    ) {
    }

    ngOnDestroy(): void {
        if (this.eventSubscriber) {
            this.eventManager.destroy(this.eventSubscriber);
        }
    }

    ngOnInit(): void {
        this.loadAttributes();
        this.registerGermplasmAttributeChanged();
        this.fileService.isFileStorageConfigured().then((isFileStorageConfigured) => this.isFileStorageConfigured = isFileStorageConfigured);
    }

    registerGermplasmAttributeChanged() {
        this.eventSubscriber = this.eventManager.subscribe('attributeChanged', (event) => {
            this.loadAttributes();
            this.germplasmDetailsContext.notifyGermplasmDetailChanges();
        });
    }

    async loadAttributes() {
        const gid = this.germplasmDetailsContext.gid;
        this.passportAttributes = await this.germplasmService.getGermplasmAttributesByGidAndType(gid, VariableTypeEnum.GERMPLASM_PASSPORT).toPromise();
        this.attributes = await this.germplasmService.getGermplasmAttributesByGidAndType(gid, VariableTypeEnum.GERMPLASM_ATTRIBUTE).toPromise();

        // Get extra info not available in the attribute entity (e.g valid values)
        const attributesByVariableId: { [key: number]: GermplasmAttribute } =
            [...this.passportAttributes, ...this.attributes]
                .reduce((prev: any, attribute) => (prev[attribute.variableId] = attribute, prev), {});
        this.variableByAttributeId = await this.variableService.filterVariables({ variableIds: Object.keys(attributesByVariableId),
            showObsoletes: true })
            .toPromise().then((variables) => {
                return variables.reduce((prev: any, variable) => {
                    prev[attributesByVariableId[Number(variable.id)].id] = variable;
                    return prev;
                }, {});
            });
    }

    editGermplasmAttribute(attributeType: number, germplasmAttribute: GermplasmAttribute): void {
        this.germplasmAttributesContext.variable = this.variableByAttributeId[germplasmAttribute.id];
        this.germplasmAttributesContext.attributeType = attributeType;
        this.germplasmAttributesContext.attribute = germplasmAttribute;
        this.router.navigate(['/', {
            outlets: {
                popup: null
            }
        }], { skipLocationChange: true, queryParamsHandling: 'merge' }).then(() => {
            this.router.navigate(['/', {
                outlets: {
                    popup: 'germplasm-attribute-dialog/' + this.germplasmDetailsContext.gid
                }
            }], {
                queryParamsHandling: 'merge',
                skipLocationChange: true
            });
        });
    }

    createGermplasmAttribute(attributeType: number): void {
        this.germplasmAttributesContext.attributeType = attributeType;

        this.router.navigate(['/', {
            outlets: {
                popup: null
            }
        }], { skipLocationChange: true, queryParamsHandling: 'merge' }).then(() => {
            this.router.navigate(['/', {
                outlets: {
                    popup: 'germplasm-attribute-dialog/' + this.germplasmDetailsContext.gid
                }
            }], {
                queryParamsHandling: 'merge',
                skipLocationChange: true
            });
        });


    }

    async deleteGermplasmAttribute(germplasmAttribute: GermplasmAttribute) {
        const germplasmResp = await this.germplasmService.getGermplasmById(this.germplasmDetailsContext.gid).toPromise();
        const germplasmUUID = germplasmResp.body.germplasmUUID;
        const variableIds = [germplasmAttribute.variableId];
        const fileCountResp = await this.fileService.getFileCount(variableIds, germplasmUUID, null).toPromise();
        const fileCount = Number(fileCountResp.headers.get('X-Total-Count'));
        if (fileCount > 0 && !await this.principal.hasAnyAuthority(MG_MANAGE_FILES_PERMISSION)) {
            this.alertService.error('germplasm-attribute-modal.delete.blocked.files');
            return;
        }

        const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
        confirmModalRef.componentInstance.message = this.translateService.instant('germplasm-attribute-modal.delete.warning', { param: germplasmAttribute.variableName });

        try {
            await confirmModalRef.result;
        } catch (e) {
            return;
        }

        try {
            if (fileCount > 0) {
                const fileOptionsModal = this.modalService.open(FileDeleteOptionsComponent as Component);
                fileOptionsModal.componentInstance.fileCount = fileCount;
                let doRemoveFiles;
                try {
                    doRemoveFiles = await fileOptionsModal.result;
                } catch (e) {
                    return;
                }
                if (doRemoveFiles) {
                    await this.fileService.removeFiles(variableIds, germplasmUUID, null).toPromise();
                } else {
                    await this.fileService.detachFiles(variableIds, germplasmUUID, null).toPromise();
                }
            }

            const result = await this.germplasmService.deleteGermplasmAttribute(this.germplasmDetailsContext.gid, germplasmAttribute.id).toPromise();
            this.alertService.success('germplasm-attribute-modal.delete.success');
            this.loadAttributes();
            this.germplasmDetailsContext.notifyGermplasmDetailChanges();
        } catch (response) {
            if (!response.error) {
                console.error(response);
                return;
            }
        }
    }

    isValidValue(attribute: GermplasmAttribute) {
        const variable = this.variableByAttributeId[attribute.id];
        const validationStatus = this.variableValidationService.isValidValue(attribute.value, variable);
        return validationStatus.isValid && validationStatus.isInRange;
    }

    openGermplasmAttributeAuditChanges(attributeType: number, germplasmAttribute: GermplasmAttribute): void {
        this.germplasmAttributesContext.attributeType = attributeType;
        this.germplasmAttributesContext.attribute = germplasmAttribute;

        this.router.navigate(['/', {
            outlets: {
                popup: null
            }
        }], { skipLocationChange: true, queryParamsHandling: 'merge' }).then(() => {
            this.router.navigate(['/', { outlets: { popup: `germplasm/${this.germplasmDetailsContext.gid}/attributes/${germplasmAttribute.id}/audit-dialog` }, }], {
                queryParamsHandling: 'merge'
            });
        });
    }

    gotoFiles(attribute: GermplasmAttribute) {
        this.router.navigate(['../files'], {
            relativeTo: this.route,
            queryParamsHandling: 'merge',
            queryParams: {
                variableName: attribute.variableName
            }
        });
    }

}
